import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import PathsPage from "@/pages/PathsPage";
import PathDetailPage from "@/pages/PathDetailPage";
import LessonPage from "@/pages/LessonPage";
import TutorPage from "@/pages/TutorPage";
import ProgressPage from "@/pages/ProgressPage";
import CertificatesPage from "@/pages/CertificatesPage";
import CoachPage from "@/pages/CoachPage";
import VerifyCertificatePage from "@/pages/VerifyCertificatePage";
import OnboardingPage from "@/pages/OnboardingPage";
import AssessmentPage from "@/pages/AssessmentPage";
import CodingLabPage from "@/pages/CodingLabPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PortfolioPage from "@/pages/PortfolioPage";
import AdminPage from "@/pages/AdminPage";
import AboutPage from "@/pages/AboutPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import StudyPlannerPage from "@/pages/StudyPlannerPage";
import NotebookPage from "@/pages/NotebookPage";
import QuizStatsPage from "@/pages/QuizStatsPage";
import EnterprisePage from "@/pages/EnterprisePage";
import HoldingsProductPage from "@/pages/HoldingsProductPage";
import PricingPage from "@/pages/PricingPage";
import LearningGoalGate from "@/components/LearningGoalGate";
import { BillingSuccessPage, BillingCancelPage } from "@/pages/BillingPages";
import { FeedbackWidget } from "@/components/FeedbackWidget";

import "@/App.css";

function AppLayout({ children }) {
  return (
    <AppShell>
      {children}
      <LearningGoalGate />
      <FeedbackWidget />
    </AppShell>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" theme="dark" />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify/:certId" element={<VerifyCertificatePage />} />
            <Route path="/portfolio/:slug" element={<PortfolioPage isPublic />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/holdings/:slug" element={<HoldingsProductPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancel" element={<BillingCancelPage />} />

            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

            <Route path="/app" element={<ProtectedRoute><AppLayout><Navigate to="/app/dashboard" replace /></AppLayout></ProtectedRoute>} />
            <Route path="/app/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/paths" element={<ProtectedRoute><AppLayout><PathsPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/paths/:slug" element={<ProtectedRoute><AppLayout><PathDetailPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/paths/:slug/assessment" element={<ProtectedRoute><AppLayout><AssessmentPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/paths/:slug/lessons/:index" element={<ProtectedRoute><AppLayout><LessonPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/tutor" element={<ProtectedRoute><AppLayout><TutorPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/tutor/:sessionId" element={<ProtectedRoute><AppLayout><TutorPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/coding" element={<ProtectedRoute><AppLayout><CodingLabPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/projects" element={<ProtectedRoute><AppLayout><ProjectsPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/projects/:projectId" element={<ProtectedRoute><AppLayout><ProjectDetailPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/analytics" element={<ProtectedRoute><AppLayout><AnalyticsPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/portfolio" element={<ProtectedRoute><AppLayout><PortfolioPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/progress" element={<ProtectedRoute><AppLayout><ProgressPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/certificates" element={<ProtectedRoute><AppLayout><CertificatesPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/coach" element={<ProtectedRoute><AppLayout><CoachPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/admin" element={<ProtectedRoute><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/settings" element={<ProtectedRoute><AppLayout><AccountSettingsPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/planner" element={<ProtectedRoute><AppLayout><StudyPlannerPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/notebook" element={<ProtectedRoute><AppLayout><NotebookPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/quiz-stats" element={<ProtectedRoute><AppLayout><QuizStatsPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/enterprise" element={<ProtectedRoute><AppLayout><EnterprisePage /></AppLayout></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
